import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

// A partir deste saldo (inclusive) o usuário passa a ver o aviso de créditos baixos
export const LIMITE_AVISO_SALDO = 20;

interface SaldoContextType {
  saldo: number;
  loading: boolean;
  error: string | null;
  /** O usuário marcou "Não mostrar mais" no aviso de saldo baixo. O banco desmarca quando o saldo aumenta. */
  avisoSilenciado: boolean;
  atualizarSaldo: (novoSaldo: number) => void;
  buscarSaldoAtual: () => Promise<number>;
  recarregarSaldo: () => Promise<void>;
  silenciarAviso: () => Promise<void>;
}

const SaldoContext = createContext<SaldoContextType | undefined>(undefined);

export function SaldoProvider({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const [saldo, setSaldo] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [avisoSilenciado, setAvisoSilenciado] = useState<boolean>(false);

  // Função para verificar se o erro é relacionado a JWT expirado
  const isJWTExpiredError = (error: any): boolean => {
    return error?.code === 'PGRST301' || 
           error?.message?.includes('JWT expired') ||
           error?.message?.includes('invalid_token') ||
           error?.status === 401;
  };

  // Função para tentar renovar a sessão
  const tentarRenovarSessao = async (): Promise<boolean> => {
    try {
      // Tentar obter sessão atualizada
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('[SaldoContext] tentarRenovarSessao: Erro ao obter sessão:', error);
        return false;
      }
      
      if (!session) {
        return false;
      }
      
      // Verificar se o token ainda é válido
      const agora = Date.now() / 1000;
      if (session.expires_at && session.expires_at <= agora) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('[SaldoContext] tentarRenovarSessao: Erro na renovação:', error);
      return false;
    }
  };

  // Função para buscar o saldo atual do servidor
  const buscarSaldoAtual = useCallback(async (): Promise<number> => {
    if (!user) {
      setLoading(false);
      return 0;
    }

    try {
      setError(null);
      
      const { data, error } = await supabase
        .from('usuarios')
        .select('saldo, aviso_saldo_baixo_silenciado')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('[SaldoContext] buscarSaldoAtual: Erro na query:', error);
        
        // NOVO: Verificar se é erro de JWT expirado
        if (isJWTExpiredError(error)) {
          console.warn('[SaldoContext] buscarSaldoAtual: JWT expirado detectado!');
          
          // Tentar renovar a sessão
          const renovouSessao = await tentarRenovarSessao();
          
          if (!renovouSessao) {
            console.error('[SaldoContext] buscarSaldoAtual: Não foi possível renovar sessão, forçando logout');
            setError('Sessão expirada. Redirecionando para login...');
            
            // Aguardar um pouco para mostrar a mensagem
            setTimeout(async () => {
              await signOut();
              window.location.href = '/';
            }, 2000);
            
            setLoading(false);
            return 0;
          }
          
          // Se renovou com sucesso, tentar buscar saldo novamente
          try {
            const { data: dataRetry, error: errorRetry } = await supabase
              .from('usuarios')
              .select('saldo, aviso_saldo_baixo_silenciado')
              .eq('id', user.id)
              .single();

            if (errorRetry) {
              console.error('[SaldoContext] buscarSaldoAtual: Erro na segunda tentativa:', errorRetry);
              throw errorRetry;
            }

            const saldoAtual = dataRetry?.saldo ?? 0;

            setAvisoSilenciado(dataRetry?.aviso_saldo_baixo_silenciado === true);
            setSaldo(saldoAtual);
            setLoading(false);
            return saldoAtual;
          } catch (retryError) {
            console.error('[SaldoContext] buscarSaldoAtual: Falha na segunda tentativa:', retryError);
            throw retryError;
          }
        }
        
        throw error;
      }
      
      const saldoAtual = data?.saldo ?? 0; // Usar nullish coalescing
      setAvisoSilenciado(data?.aviso_saldo_baixo_silenciado === true);

      // Validar se o saldo é um número válido
      if (typeof saldoAtual !== 'number' || isNaN(saldoAtual)) {
        console.warn('[SaldoContext] buscarSaldoAtual: Saldo inválido recebido:', saldoAtual, 'usando 0');
        setSaldo(0);
        setLoading(false);
        return 0;
      }
      
      setSaldo(saldoAtual);
      setLoading(false);
      return saldoAtual;
    } catch (error) {
      console.error('[SaldoContext] buscarSaldoAtual: Erro ao buscar saldo:', error);
      
      // NOVO: Verificar se é erro de JWT expirado no catch geral
      if (isJWTExpiredError(error)) {
        setError('Sessão expirada. Faça login novamente.');
        setTimeout(async () => {
          await signOut();
          window.location.href = '/';
        }, 2000);
      } else {
        setError('Erro ao carregar saldo');
      }
      
      setLoading(false);
      return 0;
    }
  }, [user, signOut]);

  // Função para recarregar saldo (útil para forçar atualização)
  const recarregarSaldo = useCallback(async (): Promise<void> => {
    setLoading(true);
    await buscarSaldoAtual();
  }, [buscarSaldoAtual]);

  // Inicializa e recarrega o saldo quando o usuário muda
  useEffect(() => {
    if (user) {
      setLoading(true);
      buscarSaldoAtual();
    } else {
      setSaldo(0);
      setAvisoSilenciado(false);
      setLoading(false);
      setError(null);
    }
  }, [user, buscarSaldoAtual]);

  // Atualiza apenas o saldo exibido. A gravação acontece no servidor, nas funções
  // `debitar_impressao` e `transferir_saldo`, que já devolvem o saldo atualizado.
  // O navegador não grava mais `saldo` direto na tabela `usuarios`.
  const atualizarSaldo = useCallback((novoSaldo: number) => {
    if (typeof novoSaldo !== 'number' || isNaN(novoSaldo) || novoSaldo < 0) {
      console.error('[SaldoContext] atualizarSaldo: Saldo inválido fornecido:', novoSaldo);
      return;
    }

    setError(null);
    setSaldo(novoSaldo);
  }, []);

  // "Não mostrar mais" no aviso de saldo baixo. Gravado no servidor (função
  // `silenciar_aviso_saldo_baixo`), que também desmarca sozinho quando o saldo aumenta.
  const silenciarAviso = useCallback(async (): Promise<void> => {
    const { error } = await supabase.rpc('silenciar_aviso_saldo_baixo');
    if (error) throw error;
    setAvisoSilenciado(true);
  }, []);

  return (
    <SaldoContext.Provider value={{
      saldo,
      loading,
      error,
      avisoSilenciado,
      atualizarSaldo,
      buscarSaldoAtual,
      recarregarSaldo,
      silenciarAviso
    }}>
      {children}
    </SaldoContext.Provider>
  );
}

export function useSaldo() {
  const context = useContext(SaldoContext);
  if (context === undefined) {
    throw new Error('useSaldo deve ser usado dentro de um SaldoProvider');
  }
  return context;
} 